import React from "react";
import { Alert, Link } from "@mui/material";
import { filter, find, some } from "~/lib/std";
import type { Topic } from "~/lqs";
import type { LoadedPlaybackSource } from "../../playback";
import type { DraftDigestionTopic } from "../../types";
import type { DraftDigestion } from "./useDraftDigestion";
import { draftTopic } from "./useDraftDigestion";

export interface TfStaticAlertProps {
  playbackTopics: Topic[];
  playerBounds: LoadedPlaybackSource["bounds"];
  draftDigestionTopics: DraftDigestionTopic[];
  dispatch: DraftDigestion["dispatch"];
}

export default function TfStaticAlert({
  playbackTopics,
  playerBounds,
  draftDigestionTopics,
  dispatch,
}: TfStaticAlertProps) {
  const tfStaticTopic = find(playbackTopics, { name: "/tf_static" });

  if (tfStaticTopic === undefined) {
    // /tf_static isn't in this log
    return null;
  }

  if (some(draftDigestionTopics, { topicId: tfStaticTopic.id })) {
    // /tf_static is already in digestion
    return null;
  }

  // Draft list only includes topic IDs, so need to use those to find list
  // of actual topics
  const draftedTopics = filter(playbackTopics, ({ id }) =>
    some(draftDigestionTopics, { topicId: id }),
  );

  if (!mightNeedTfStatic(draftedTopics)) {
    // Of the drafted topics, none merit showing the user an alert
    return null;
  }

  return (
    <Alert severity="info" variant="filled">
      Some topics in your digestion may need the <code>/tf_static</code> topic.
      If you need this topic, you can{" "}
      <Link
        component="button"
        color="inherit"
        onClick={() =>
          dispatch(draftTopic({ topic: tfStaticTopic, range: playerBounds }))
        }
      >
        add it to your digestion
      </Link>
      .
    </Alert>
  );
}

const SENSOR_MSGS_RE = /^sensor_msgs\/\w/;

function mightNeedTfStatic(draftedTopics: Topic[]): boolean {
  return some(draftedTopics, (draftedTopic) => {
    const { name, typeName } = draftedTopic;

    if (name === "/tf") {
      return true;
    }

    if (typeName === null) {
      return false;
    }

    return SENSOR_MSGS_RE.test(typeName);
  });
}
